/**
 * usePriceCheck — Hook léger pour interroger /api/expenses/price-check
 *
 * Debounce 500ms sur la description et le supplier.
 * Retourne { data, loading, alert } où alert est :
 *   - null : pas d'alerte (ok ou pas assez de données)
 *   - { level: "warn" | "high", diff_pct, last, avg } si PU > avg × (1 + threshold)
 */
import { useEffect, useState } from "react";
import axios from "axios";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

/**
 * @param description string
 * @param unitPrice   number      current unit price the user has typed
 * @param supplier    string?     optional supplier filter
 * @param threshold   number      default 0.20 → alert if price is 20 % above the average
 */
export default function usePriceCheck({ description, unitPrice, supplier = "", threshold = 0.2 } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const desc = (description || "").trim();
    if (desc.length < 3) {
      setData(null);
      return;
    }
    const t = setTimeout(() => {
      setLoading(true);
      axios.get(`${API}/expenses/price-check`, {
        params: { description: desc, supplier: supplier || undefined },
      })
        .then(r => setData(r.data))
        .catch(() => setData(null))
        .finally(() => setLoading(false));
    }, 500);
    return () => clearTimeout(t);
  }, [description, supplier]);

  let alert = null;
  const up = parseFloat(unitPrice) || 0;
  if (data && data.count && up > 0 && data.avg_price > 0) {
    const diff = (up - data.avg_price) / data.avg_price;
    if (diff >= threshold) {
      alert = {
        level: diff >= threshold * 2 ? "high" : "warn",
        diff_pct: Math.round(diff * 100),
        avg: data.avg_price,
        last: data.last_price,
        max: data.max_price,
      };
    }
  }

  return { data, loading, alert };
}
